import logger from 'js-logger';

import { platformInfo } from '@gem/utils';

import { e2eeCache } from 'APP/cache/e2ee';

class WebCryptoServiceClass {
  private clientId: string | null = null;
  private cryptoKey: CryptoKey;
  private iv: Uint8Array;
  public isInitialized = false;

  async init(userId: string): Promise<void> {
    const deviceId = platformInfo.deviceId;
    this.clientId = `${userId}:${deviceId}`;
    const cryptoKeys = await e2eeCache.getWebCryptoKeys(this.clientId);

    if (cryptoKeys) {
      this.cryptoKey = await window.crypto.subtle.importKey(
        'raw',
        cryptoKeys.cryptoKey,
        { name: 'AES-GCM' },
        true,
        ['encrypt', 'decrypt']
      );
      this.iv = cryptoKeys.iv;

      return;
    }

    const cryptoKey = await window.crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256,
      },
      true,
      ['encrypt', 'decrypt']
    );
    const cryptoKeyArrayBuffer = await window.crypto.subtle.exportKey('raw', cryptoKey);
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    this.iv = iv;
    this.cryptoKey = cryptoKey;

    e2eeCache.putWebCryptoKeys(this.clientId, {
      cryptoKey: cryptoKeyArrayBuffer,
      iv,
    });

    this.isInitialized = true;
  }

  clearStorages(): void {
    if (this.clientId) {
      e2eeCache.putWebCryptoKeys(this.clientId, null);
    }
  }

  async encodeMessage(message: ArrayBuffer): Promise<ArrayBuffer | null> {
    try {
      return await window.crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: this.iv,
        },
        this.cryptoKey,
        message
      );
    } catch (e) {
      logger.get('WebCryptoService').error('WebCryptoService.encodeMessage', e);
      return null;
    }
  }

  async parseMessage(cipher: ArrayBuffer): Promise<ArrayBuffer | null> {
    try {
      return await window.crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: this.iv,
        },
        this.cryptoKey,
        cipher
      );
    } catch (e) {
      return null;
    }
  }
}

export const WebCryptoService = new WebCryptoServiceClass();
